import * as React from 'react';
import { useMemo, useReducer } from 'react';

import { SelectedServicesState } from '../Checkout/store/selectedServices/reducers';
import { PassengerState } from '../Checkout/store/passengers/reducers';
import {
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment
} from '@websky/graphql';
import { Money } from '../Money/types';
import { multiplyMoney, getTotalPrice } from '@shared/lib/money';

import { getVipServiceStateSelectedKeys } from './utils';

type OrderServiceFragment =
	| GdsService_OrderAdditionalServiceGdsServiceVip_Fragment
	| GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment;

export interface Reducer {
	[segmentId: string]: {
		[passengerId: string]: number;
	};
}

interface Action {
	passengerId: string;
	segmentId: string;
	count: number;
}

const serviceReducer: React.Reducer<Reducer, Action> = (state, action) => {
	const { segmentId, passengerId, count } = action;

	if (state[segmentId]?.[passengerId] === count) {
		return state;
	}

	return {
		...state,
		[segmentId]: {
			...state[segmentId],
			[passengerId]: count
		}
	};
};

const createInitialState = (
	passengers: PassengerState[],
	services: OrderServiceFragment[],
	selectedServices?: SelectedServicesState
): Reducer => {
	const state: Reducer = {};

	if (!selectedServices) {
		return state;
	}

	passengers.forEach(passenger => {
		const passengerServices = selectedServices[passenger.id];
		if (!passengerServices) {
			return;
		}

		services.forEach(service => {
			const serviceData = passengerServices[service.id];
			if (!serviceData) {
				return;
			}

			serviceData.forEach(selectedService => {
				selectedService.segments.forEach(segment => {
					if (!state[segment]) {
						state[segment] = {};
					}
					state[segment][passenger.id] = selectedService.number;
				});
			});
		});
	});

	return state;
};

const findMinPrice = (services: OrderServiceFragment[]): Money | null => {
	if (services.length === 0) {
		return null;
	}

	return services.reduce(
		(min, service) => (service.price.amount < min.amount ? service.price : min),
		services[0].price
	);
};

const findServiceByPassengerAndSegment = (
	services: OrderServiceFragment[],
	segmentId: string,
	passengerId: string
): OrderServiceFragment | undefined => {
	return services.find(
		service =>
			service.allowedSegments.some(ids => ids.includes(segmentId)) &&
			service.allowedPassengers.some(ids => ids.includes(passengerId))
	);
};

const calculateTotalPrice = (state: Reducer, services: OrderServiceFragment[]): Money | null => {
	const servicePrices: Money[] = [];

	for (const segmentId in state) {
		for (const passengerId in state[segmentId]) {
			const count = state[segmentId][passengerId];

			if (count <= 0) {
				continue;
			}

			const serviceByPassengerId = findServiceByPassengerAndSegment(services, segmentId, passengerId);

			const servicePrice = serviceByPassengerId?.price ?? services[0]?.price;

			if (servicePrice) {
				servicePrices.push(multiplyMoney(servicePrice, count));
			}
		}
	}

	if (servicePrices.length === 0) {
		return null;
	}

	const totalPrice = getTotalPrice(servicePrices, price => price);

	return totalPrice.amount > 0 ? totalPrice : null;
};

const compareStates = (state1: Reducer, state2: Reducer): boolean => {
	const keys1 = getVipServiceStateSelectedKeys(state1);
	const keys2 = getVipServiceStateSelectedKeys(state2);

	if (keys1.length !== keys2.length) {
		return false;
	}

	const sorted1 = [...keys1].sort();
	const sorted2 = [...keys2].sort();

	return sorted1.every((key, index) => key === sorted2[index]);
};

export const useVipServiceState = (
	passengers: PassengerState[],
	services: OrderServiceFragment[],
	selectedServices?: SelectedServicesState
) => {
	const initialState = useMemo(() => createInitialState(passengers, services, selectedServices), [
		passengers,
		services,
		selectedServices
	]);

	const [state, dispatch] = useReducer(serviceReducer, initialState);

	const minPrice = useMemo(() => findMinPrice(services), [services]);

	const totalPrice = useMemo(() => {
		if (Object.keys(state).length === 0 || services.length === 0) {
			return null;
		}

		return calculateTotalPrice(state, services);
	}, [state, services]);

	const isDifferentState = useMemo(() => !compareStates(initialState, state), [initialState, state]);

	return {
		state,
		minPrice,
		totalPrice,
		isDifferentState,
		dispatch
	};
};
